import { useCallback, useEffect, useRef, useState } from "react"

import { logger } from "@/lib/logger"

const log = logger("CollapsedSidebarSections")

/**
 * One key for the whole sidebar, versioned so a future shape change discards
 * old snapshots rather than half-reading them.
 *
 * Deliberately NOT scoped to organization or workspace: a fold is a statement
 * about the sidebar, which is the same sidebar everywhere, and re-expanding
 * every section on an org switch would be the surprise rather than the safe
 * default. Nothing tenant-specific is stored either — the keys are section
 * names the navigation already declares in code.
 */
const STORAGE_KEY = "coval:sidebar:collapsed-sections:v1"

type CollapsedMap = Record<string, boolean>

/**
 * Only the folded entries are kept, so a stale key for a section that no
 * longer exists costs nothing and an unexpected value is dropped rather than
 * trusted.
 */
function readStored(): CollapsedMap {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) {
      return {}
    }
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {}
    }
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).filter(
        ([, value]) => value === true,
      ),
    ) as CollapsedMap
  } catch (error: unknown) {
    log.warn("Could not read collapsed sidebar sections", { error })
    return {}
  }
}

function writeStored(collapsed: CollapsedMap): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(collapsed))
  } catch (error: unknown) {
    // A fold that cannot be remembered still folds.
    log.warn("Could not persist collapsed sidebar sections", { error })
  }
}

/**
 * Which sidebar sections the user has folded away, remembered across
 * navigations, reloads, and sessions.
 *
 * Nothing is read during render: the server render is real, so the first paint
 * is always "everything expanded" and the stored folds land on mount. That
 * costs a frame and buys a sidebar that hydrates without disagreeing with the
 * HTML it was served.
 *
 * Every write merges into what storage currently holds rather than dumping this
 * instance's own map, because several instances share the key — the shell's own
 * sections and Sofia's chat section each know only their own keys, and a blind
 * write from one would drop the other's. The in-memory map is merged in too, so
 * a browser that refuses storage still behaves for the rest of the session.
 */
export function useCollapsedSidebarSections(): {
  isCollapsed: (sectionKey: string) => boolean
  toggle: (sectionKey: string) => void
} {
  const [collapsed, setCollapsed] = useState<CollapsedMap>({})
  // Read by `toggle` without making it depend on — and be rebuilt by — state.
  const collapsedRef = useRef<CollapsedMap>(collapsed)
  collapsedRef.current = collapsed

  useEffect(() => {
    const stored = readStored()
    if (Object.keys(stored).length > 0) {
      setCollapsed((current) => ({ ...current, ...stored }))
    }
  }, [])

  const toggle = useCallback((sectionKey: string) => {
    const next = { ...collapsedRef.current, ...readStored() }
    if (next[sectionKey]) {
      delete next[sectionKey]
    } else {
      next[sectionKey] = true
    }
    writeStored(next)
    setCollapsed(next)
  }, [])

  const isCollapsed = useCallback(
    (sectionKey: string) => Boolean(collapsed[sectionKey]),
    [collapsed],
  )

  return { isCollapsed, toggle }
}
